import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';

import { AuthStore } from '../../store/auth.store';

/**
 * Cleans up stale kc-callback-* entries from localStorage.
 * These entries accumulate from incomplete OAuth flows and should be removed
 * if they have expired or contain invalid JSON.
 */
function cleanStaleKeycloakCallbacks(): void {
  const keysToRemove: string[] = [];
  const now = Date.now();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('kc-callback-')) {
      try {
        const value = JSON.parse(localStorage.getItem(key) || '{}');
        if (value.expires && value.expires < now) {
          keysToRemove.push(key);
        }
      } catch {
        // Invalid JSON, remove it
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log('[AuthInit] Removed stale OAuth callback:', key);
  });

  if (keysToRemove.length > 0) {
    console.log(`[AuthInit] Cleaned ${keysToRemove.length} stale kc-callback-* entries`);
  }
}

export function initializeAuth() {
  return async () => {
    // Exception page is fully public - skip all auth initialization
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/exception')) {
      console.log('[AuthInit] On exception page - skipping Keycloak initialization');
      return;
    }

    // Clean up stale Keycloak callback entries from localStorage
    cleanStaleKeycloakCallbacks();

    // Check for Keycloak error in URL (OAuth redirect failures)
    const urlParams = new URLSearchParams(window.location.search);
    const keycloakError = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (keycloakError) {
      console.error('[AuthInit] Keycloak OAuth error detected:', keycloakError, errorDescription);

      // Clear URL params to prevent loops
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      // Clear any stale auth state
      try {
        const keycloak = inject(Keycloak);
        keycloak.clearToken();
        console.log('[AuthInit] Cleared stale token after OAuth error');
      } catch (e) {
        console.warn('[AuthInit] Could not clear token:', e);
      }
    }

    const authStore = inject(AuthStore);
    await authStore.initialize();
  };
}
